import type { MembershipDto, TokenPair } from '@aawas/contracts';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';
const SESSION_KEY = 'aawas.session';
const ACTIVE_KEY = 'aawas.active';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
  /** True when the request never reached the server (offline, DNS, timeout). */
  get isNetwork() {
    return this.status === 0;
  }
}

export interface ActiveContext {
  communityId: string;
  role: MembershipDto['role'];
}

// ───────────── session (kept in localStorage; the access token is short-lived) ─────────────

let session: TokenPair | null = read<TokenPair>(SESSION_KEY);
let active: ActiveContext | null = read<ActiveContext>(ACTIVE_KEY);
const listeners = new Set<() => void>();

function read<T>(key: string): T | null {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
}
function write(key: string, v: unknown) {
  try {
    if (v === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* private mode / quota: session lives in memory only */
  }
}
const emit = () => listeners.forEach((l) => l());

export const subscribeSession = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
export const getSession = () => session;
export const getActive = () => active;

export function setSession(s: TokenPair | null) {
  session = s;
  write(SESSION_KEY, s);
  if (!s) setActive(null);
  else if (!active || !s.memberships.some((m) => m.communityId === active!.communityId && m.role === active!.role)) {
    const first = pickDefault(s.memberships);
    setActive(first ? { communityId: first.communityId, role: first.role } : null);
  }
  emit();
}

export function setActive(a: ActiveContext | null) {
  active = a;
  write(ACTIVE_KEY, a);
  emit();
}

const PRIORITY: MembershipDto['role'][] = ['admin', 'guard', 'staff', 'owner', 'tenant', 'family'];
export const pickDefault = (ms: MembershipDto[]) => [...ms].sort((a, b) => PRIORITY.indexOf(a.role) - PRIORITY.indexOf(b.role))[0];

// ───────────── requests ─────────────

/**
 * ok          the login was renewed (or another tab already renewed it)
 * invalid     the server says this login is finished (revoked, expired, signed out elsewhere): the ONLY case that signs the user out
 * unavailable we could not find out (offline, server restarting, 5xx, rate limit): keep the login and try again later
 */
export type Refreshed = 'ok' | 'invalid' | 'unavailable';
let refreshing: Promise<Refreshed> | null = null;

const fresh = (s: TokenPair | null) => !!s && new Date(s.accessExpiresAt).getTime() - Date.now() > 30_000;

async function refreshSession(): Promise<Refreshed> {
  if (!session) return 'invalid';
  // Single-flight inside this tab: parallel 401s share one rotation (refresh tokens are single-use).
  refreshing ??= (async () => {
    const attempt = async (): Promise<Refreshed> => {
      // Another tab may have refreshed while we waited for the lock (all tabs share one login). Adopt its result
      // instead of spending our own token, which would look like a replay.
      const stored = read<TokenPair>(SESSION_KEY);
      if (!stored) {
        if (session) setSession(null); // signed out in another tab
        return 'invalid';
      }
      if (stored.refreshToken !== session?.refreshToken) {
        session = stored;
        emit();
        if (fresh(stored)) return 'ok';
      }
      const used = stored.refreshToken;
      try {
        const res = await fetch(`${BASE}/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken: used }),
        });
        if (res.ok) {
          setSession((await res.json()) as TokenPair);
          return 'ok';
        }
        if (res.status === 401 || res.status === 403) {
          // Only sign out if nobody else has renewed the login in the meantime.
          const latest = read<TokenPair>(SESSION_KEY);
          if (latest && latest.refreshToken !== used) {
            session = latest;
            emit();
            return 'ok';
          }
          setSession(null); // genuinely revoked / expired
          return 'invalid';
        }
        return 'unavailable'; // 5xx, 429, a proxy error page while the server restarts: not an answer about the login
      } catch {
        return 'unavailable'; // offline
      }
    };
    try {
      // Web Locks make the refresh exclusive across all tabs and the installed app of this browser.
      return navigator.locks ? await navigator.locks.request('aawas-refresh', attempt) : await attempt();
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

// Keep every tab on the same login: renewals, sign-in and sign-out in one tab reach the others.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== SESSION_KEY) return;
    session = read<TokenPair>(SESSION_KEY);
    emit();
  });
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  form?: FormData;
  /** Skip the Authorization header (login endpoints). */
  anon?: boolean;
  /** Skip the community/role headers (endpoints that are user-level, e.g. /me). */
  noCommunity?: boolean;
  signal?: AbortSignal;
}

export async function api<T>(path: string, o: RequestOptions = {}): Promise<T> {
  if (!o.anon && session && new Date(session.accessExpiresAt).getTime() - Date.now() < 30_000) {
    if ((await refreshSession()) === 'unavailable') {
      throw new ApiError(0, 'NETWORK', 'Cannot reach the server right now. Trying again shortly.');
    }
  }
  const attempt = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (o.body !== undefined) headers['content-type'] = 'application/json';
    if (!o.anon && session) headers.authorization = `Bearer ${session.accessToken}`;
    if (!o.anon && !o.noCommunity && active) {
      headers['x-community-id'] = active.communityId;
      headers['x-role'] = active.role;
    }
    try {
      return await fetch(`${BASE}/v1${path}`, {
        method: o.method ?? (o.body !== undefined || o.form ? 'POST' : 'GET'),
        headers,
        body: o.form ?? (o.body !== undefined ? JSON.stringify(o.body) : undefined),
        signal: o.signal ?? AbortSignal.timeout(20_000),
      });
    } catch {
      throw new ApiError(0, 'NETWORK', 'You appear to be offline. Check your connection.');
    }
  };

  let res = await attempt();
  if (res.status === 401 && !o.anon) {
    const body = await res.clone().json().catch(() => null);
    if (body?.error?.code === 'TOKEN_INVALID') {
      const r = await refreshSession();
      if (r === 'ok') res = await attempt();
      else if (r === 'unavailable') throw new ApiError(0, 'NETWORK', 'Cannot reach the server right now. Trying again shortly.');
    }
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  const data = ct.includes('application/json') ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    // A 401 alone never signs the user out (it can just be an expired access token). Ending the session is decided
    // only by the refresh outcome above, or by the server explicitly saying no credentials were sent.
    if (res.status === 401 && data?.error?.code === 'UNAUTHORIZED' && !o.anon) setSession(null);
    throw new ApiError(res.status, data?.error?.code ?? 'ERROR', data?.error?.message ?? 'Something went wrong', data?.error?.details);
  }
  return data as T;
}

/** Fetches a protected image and returns an object URL (needs the auth headers, so <img src> won't do). */
export async function fetchBlobUrl(path: string): Promise<string> {
  const headers: Record<string, string> = {};
  if (session) headers.authorization = `Bearer ${session.accessToken}`;
  if (active) {
    headers['x-community-id'] = active.communityId;
    headers['x-role'] = active.role;
  }
  const res = await fetch(`${BASE}/v1${path}`, { headers });
  if (!res.ok) throw new ApiError(res.status, 'ERROR', 'Could not load image');
  return URL.createObjectURL(await res.blob());
}

/** Downloads a protected file (needs the auth headers, so a plain link will not do). */
export async function downloadFile(path: string, filename: string): Promise<void> {
  if (session && new Date(session.accessExpiresAt).getTime() - Date.now() < 30_000) await refreshSession();
  const headers: Record<string, string> = {};
  if (session) headers.authorization = `Bearer ${session.accessToken}`;
  if (active) {
    headers['x-community-id'] = active.communityId;
    headers['x-role'] = active.role;
  }
  let res: Response;
  try {
    res = await fetch(`${BASE}/v1${path}`, { headers });
  } catch {
    throw new ApiError(0, 'NETWORK', 'You appear to be offline. Check your connection.');
  }
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new ApiError(res.status, data?.error?.code ?? 'ERROR', data?.error?.message ?? 'Download failed');
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const wsUrl = () => {
  const base = BASE || window.location.origin;
  return `${base.replace(/^http/, 'ws')}/v1/realtime`;
};

export async function signOut() {
  const s = session;
  setSession(null);
  if (s) await fetch(`${BASE}/v1/auth/logout`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ refreshToken: s.refreshToken }) }).catch(() => undefined);
}
